import { Input } from 'antd';
import dayjs from 'dayjs';
import * as React from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { MessageStates } from 'containers/MessageBox';
import { turnMessageOn } from 'store/app/actions';
import { ActivityType } from 'store/app/enums';
import {
  addExcelActivityNoteRequest,
  fetchExcelActivityLogsRequest,
} from 'store/organisation/actions';
import type { StoreState } from 'store/types';
import type { ActivityLog } from 'store/vat/models';
import { sanitizeActivityLogHtml } from 'utils/sanitize-html';
import { useOverlayBackHandler } from 'utils/useOverlayBackHandler';
import { usePwaBottomSheetDragClose } from 'utils/usePwaBottomSheetDragClose';
import { PwaIcon, type PwaIconName } from './Icon';
import { PwaSheet } from './ui';

export type PwaReviewActivitySheetProps = {
  open: boolean;
  onClose: () => void;
  organisationId: string;
  /** Optional subtitle context (entity / document name). */
  contextLabel?: string;
};

const iconForType = (
  type: ActivityType | string
): { name: PwaIconName; tone: '' | 'danger' | 'note' } => {
  if (type === ActivityType.FILE_DELETE || type === ActivityType.ROW_DELETE) {
    return { name: 'trash', tone: 'danger' };
  }
  if (type === ActivityType.FILE_UPLOAD) {
    return { name: 'upload', tone: '' };
  }
  if (type === ActivityType.NOTE) {
    return { name: 'note', tone: 'note' };
  }
  if (type === ActivityType.REFRESHED) {
    return { name: 'refresh', tone: '' };
  }
  return { name: 'history', tone: '' };
};

const stripHtml = (html: string) =>
  html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

export const PwaReviewActivitySheet: React.FC<PwaReviewActivitySheetProps> = ({
  open,
  onClose,
  organisationId,
  contextLabel,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dragZoneRef = React.useRef<HTMLDivElement | null>(null);
  const [noteOpen, setNoteOpen] = React.useState(false);
  const [noteText, setNoteText] = React.useState('');

  useOverlayBackHandler(open, onClose, 'pwa-review-activity-sheet');
  const dragClose = usePwaBottomSheetDragClose(open, onClose, dragZoneRef);

  const organisation = useSelector((state: StoreState) =>
    state.organisation.organisations?.find((org) => org.uuid === organisationId)
  );
  const activityLogs: ActivityLog[] = organisation?.activityLogs ?? [];

  React.useEffect(() => {
    if (!open || !organisationId) return;
    dispatch(fetchExcelActivityLogsRequest(organisationId));
  }, [dispatch, open, organisationId]);

  React.useEffect(() => {
    if (!open) {
      setNoteOpen(false);
      setNoteText('');
    }
  }, [open]);

  const saveNote = React.useCallback(() => {
    if (!noteText.trim()) {
      dispatch(
        turnMessageOn({
          title: t('main.reconciliation-empty-note-title'),
          description: t('main.reconciliation-empty-note-desc'),
          type: MessageStates.WARNING,
        })
      );
      return;
    }
    dispatch(addExcelActivityNoteRequest(organisationId, noteText.trim()));
    setNoteText('');
    setNoteOpen(false);
  }, [dispatch, noteText, organisationId, t]);

  const exportRows = React.useMemo(
    () =>
      activityLogs.map((log) => ({
        type: String(log.type ?? ''),
        createdAt: dayjs(log.createdAt).format('DD/MM/YYYY - HH:mm:ss'),
        details: stripHtml(log.details || ''),
        email: log.email,
        fullName: log.fullName,
      })),
    [activityLogs]
  );

  const exportHeaders = [
    { label: 'type', key: 'type' },
    { label: 'createdAt', key: 'createdAt' },
    { label: 'details', key: 'details' },
    { label: 'email', key: 'email' },
    { label: 'fullName', key: 'fullName' },
  ];

  if (!open) return null;

  const sub = contextLabel
    ? t('organisation.pwa-review-activity-sub', {
        defaultValue: '{{context}} · {{count}} events',
        context: contextLabel,
        count: activityLogs.length,
      })
    : t('organisation.pwa-review-activity-sub-count', {
        defaultValue: '{{count}} events',
        count: activityLogs.length,
      });

  return (
    <PwaSheet
      full
      className="pwa-review-activity-sheet"
      title={t('organisation.reconciliation-activity', {
        defaultValue: 'Activity',
      })}
      sub={sub}
      onClose={onClose}
      closeLabel={t('common.close', { defaultValue: 'Close' })}
      dragZoneRef={dragZoneRef}
      dragCloseHandlers={dragClose}
      footer={
        <>
          <CSVLink
            filename="activity-logs.csv"
            data={exportRows}
            headers={exportHeaders}
            className="pwa-btn secondary"
            style={{ textDecoration: 'none' }}
          >
            <PwaIcon name="download" size={19} />
            {t('vat-report.export-activity', { defaultValue: 'Export' })}
          </CSVLink>
          <button
            type="button"
            className="pwa-btn primary"
            onClick={() => setNoteOpen((v) => !v)}
          >
            <PwaIcon name="note" size={19} />
            {noteOpen
              ? t('organisation.reconciliation-close-note', {
                  defaultValue: 'Close note',
                })
              : t('common.add-note', { defaultValue: 'Add note' })}
          </button>
        </>
      }
    >
      {noteOpen ? (
        <div className="pwa-review-activity-note">
          <Input.TextArea
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder={t('organisation.reconciliation-note-placeholder', {
              defaultValue: 'Write a note…',
            })}
          />
          <button
            type="button"
            className="pwa-btn primary"
            style={{ width: '100%', marginTop: 10 }}
            onClick={saveNote}
          >
            {t('organisation.reconciliation-save-note', {
              defaultValue: 'Save note',
            })}
          </button>
        </div>
      ) : null}

      <div className="pwa-timeline" style={{ paddingTop: 4 }}>
        {activityLogs.length === 0 ? (
          <div className="pwa-row static">
            <span className="pwa-row-body">
              <span className="pwa-row-sub">
                {t('vat-report.no-activity-logs-available', {
                  defaultValue: 'No activity yet.',
                })}
              </span>
            </span>
          </div>
        ) : (
          activityLogs.map((log, index) => {
            const ico = iconForType(log.type);
            const when = dayjs(log.createdAt).format('D MMM YYYY · HH:mm');
            const details = log.details || '';
            const isHtml = details.includes('<') && details.includes('>');
            return (
              <div
                key={`${log.createdAt}-${log.type}-${index}`}
                className="pwa-tl"
              >
                <span className={`pwa-tl-ico${ico.tone ? ` ${ico.tone}` : ''}`}>
                  <PwaIcon name={ico.name} size={16} stroke={2} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <p>
                    <b>{log.fullName || log.email || '—'}</b>{' '}
                    {String(log.type ?? '')}
                  </p>
                  <span className="pwa-row-meta">{when}</span>
                  {details ? (
                    isHtml ? (
                      <div
                        className="file"
                        style={{ color: 'var(--gt-ink)', whiteSpace: 'normal' }}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeActivityLogHtml(details),
                        }}
                      />
                    ) : (
                      <div
                        className="file"
                        style={{ color: 'var(--gt-ink)', whiteSpace: 'normal' }}
                      >
                        <span style={{ whiteSpace: 'normal' }}>{details}</span>
                      </div>
                    )
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>
    </PwaSheet>
  );
};

export default PwaReviewActivitySheet;
